/*
Session teardown: the single place that ends a session, whether the user
clicked Logout or the axios interceptor forced them out on a terminal 401.

XOL-446. Before this module, neither path cleaned up:

  * `resetStore()` reset 5 of ~18 Vuex keys.
  * `sessionStorage['xolas-config']` (the whole org-scoped system/config
    payload) was never cleared.
  * `localStorage['xolas-user']` (the whole Vuex blob) was overwritten,
    never removed.
  * The forced-401 path looked like a hard navigation but is not:
    `window.location.assign('/#/login')` from pathname `/` differs from the
    current URL only in the fragment, so it is a fragment navigation with no
    document unload. Module-scope caches survived it.

The practical symptom was a dev recreating the local DB and then having to
"Clear site data" by hand, because every cached id pointed at rows that no
longer existed.

## Design

`endSession()` is the contract; the reload is enforcement. Both run.

The reload alone is not enough: there is a window between "storage wiped"
and "document unloaded" in which an in-flight XHR callback can call
`saveStore()` and write user #1's state straight back into the key we just
cleared. `isTearingDown()` closes that window, and `main.js`'s `saveStore()`
consults it. jsdom also cannot reload, so the in-memory reset is the part
the unit suite can actually assert.

The reset-hook registry exists because a central list of "things to clear"
does not survive contact with a growing codebase. `lastForcedLogoutWasGate`
in axiosClient.js is the proof: it shipped with no reset and has been
silently suppressing the session-expired banner for the life of the page
ever since. A module that owns a cache registers its own reset next to the
`let` that declares it, in the file its author is already looking at.

## Dependency direction

This is a leaf. It imports `auth.js` and the config defaults, and NEVER
imports `axiosClient.js`, `main.js`, or any `.vue`. `main.js` imports
`axiosClient.js`, so `axiosClient.js` can never import `main.js` — routing
the shared teardown through here is what lets both logout paths use it.
State this module does not own is reached two ways: `setStore()` (the same
handshake `authLib.setStore()` uses) and `registerResetHook()`.

Babel 6: no optional chaining, no nullish coalescing, no `.finally()`.
------------------------------------------------------------------------ */

import * as authLib from '@/lib/auth'
import { makeDefaultStore } from '@/components/config/store'

// Every browser-storage key the SPA owns. The teardown sweeps by prefix
// rather than by this list (so a future key is covered without editing the
// sweep), but the names live here so there is one place to look.
export const STORAGE_PREFIX = 'xolas-'

export const USER_STATE_KEY = 'xolas-user'
export const CONFIG_CACHE_KEY = 'xolas-config'
export const POST_LOGIN_REDIRECT_KEY = 'xolas-post-login-redirect'
export const LOGOUT_REASON_KEY = 'xolas-logout-reason'
export const TEARDOWN_FUSE_KEY = 'xolas-teardown-fuse'

/**
 * sessionStorage keys that survive a teardown.
 *
 * The post-login redirect is preserved deliberately: the user asked to keep
 * it even though it will usually not resolve on the next login. The logout
 * reason is preserved so Login.vue can still render its banner on the other
 * side of the reload. Both survive a reload for free — sessionStorage is
 * per-tab and persists across same-tab navigations.
 *
 * The sweep works by prefix and simply skips these, so the guarantee does
 * not depend on call ordering. This constant is also what the unit spec
 * asserts against, so a future broadening of the sweep gets caught.
 */
export const PRESERVED_SESSION_KEYS = [
  POST_LOGIN_REDIRECT_KEY,
  LOGOUT_REASON_KEY
]

// A hard teardown within this window degrades to a soft one. Without it, a
// boot request that 401s on every load would reload, 401 again, and loop.
export const FUSE_WINDOW_MS = 10000

let storeRef = null
let tearingDown = false
const resetHooks = []

/**
 * Wire the Vuex store. Called from main.js right after the store is built,
 * beside `authLib.setStore(store)`.
 */
export function setStore (store) {
  storeRef = store
}

/**
 * Register an in-memory reset. Call this at module scope, at import time,
 * from the file that owns the cache being reset.
 */
export function registerResetHook (name, fn) {
  if (typeof fn !== 'function') {
    return
  }
  resetHooks.push({ name: name, fn: fn })
}

/**
 * True while a teardown is in progress. `saveStore()` checks this and
 * no-ops, so a late XHR callback cannot resurrect the state being cleared.
 */
export function isTearingDown () {
  return tearingDown
}

function safeLocalStorage () {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      return window.localStorage
    }
  } catch (e) {
    // private mode / disabled storage
  }
  return null
}

function safeSessionStorage () {
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      return window.sessionStorage
    }
  } catch (e) {
    // private mode / disabled storage
  }
  return null
}

/**
 * Build the state the app boots with: a private copy of the defaults, the
 * persisted blob merged over it, then the environment-derived fields
 * re-applied on top.
 *
 * This is used at boot AND at reset, which is the point — before XOL-446
 * the env block existed only in main.js's boot path, so `resetStore()`
 * silently dropped `endpoint` / `hostname` / `copyright`. One implementation
 * means "the reset re-applies boot fields" is true by construction rather
 * than by two copies staying in sync.
 *
 * `env` is passed in rather than read here so this module stays free of
 * build-time globals and stays testable.
 */
export function buildBootState (persisted, env) {
  var state = makeDefaultStore()

  if (persisted && typeof persisted === 'object') {
    for (var k in persisted) {
      if (Object.prototype.hasOwnProperty.call(persisted, k)) {
        state[k] = persisted[k]
      }
    }
  }

  // The auth slice is never restored from the persisted blob — tokens are
  // owned by lib/auth.js and hydrated from their own localStorage keys.
  // stripSensitiveState should already have kept them out, but a blob
  // written by an older build could still carry them.
  state.auth = makeDefaultStore().auth

  if (env && typeof env === 'object') {
    for (var e in env) {
      if (Object.prototype.hasOwnProperty.call(env, e)) {
        state[e] = env[e]
      }
    }
  }

  return state
}

function readPreservedSessionKeys () {
  var out = {}
  var ss = safeSessionStorage()
  if (!ss) {
    return out
  }
  for (var i = 0; i < PRESERVED_SESSION_KEYS.length; i++) {
    var key = PRESERVED_SESSION_KEYS[i]
    try {
      var value = ss.getItem(key)
      if (value !== null && value !== undefined) {
        out[key] = value
      }
    } catch (e) {
      // ignore
    }
  }
  return out
}

function restorePreserved (preserved) {
  var ss = safeSessionStorage()
  if (!ss || !preserved) {
    return
  }
  for (var key in preserved) {
    if (Object.prototype.hasOwnProperty.call(preserved, key)) {
      try {
        ss.setItem(key, preserved[key])
      } catch (e) {
        // ignore quota / private-mode failures
      }
    }
  }
}

/**
 * Remove every `xolas-` prefixed key from a storage area.
 *
 * Prefix sweep, not `.clear()`: in production the SPA shares an origin with
 * the API, so blanket-clearing would be overreach. Prefix sweep, not a
 * hardcoded key list: a future `xolas-something` is then covered without
 * anyone remembering to edit this function.
 *
 * Note this also clears `xolas-jwks`. That is deliberate and matters
 * specifically for the DB-swap case this ticket is about — a recreated
 * database has new RSA signing keys, and a stale JWKS cache would fail
 * verification until its 6h TTL expires or a kid-miss forces a refetch.
 * Re-fetching costs one request.
 */
export function wipeStorageByPrefix (storage, skipKeys) {
  if (!storage) {
    return []
  }
  var skip = skipKeys || []
  var doomed = []
  try {
    for (var i = 0; i < storage.length; i++) {
      var key = storage.key(i)
      if (key === null || key === undefined) {
        continue
      }
      if (key.indexOf(STORAGE_PREFIX) !== 0) {
        continue
      }
      if (skip.indexOf(key) !== -1) {
        continue
      }
      doomed.push(key)
    }
    for (var j = 0; j < doomed.length; j++) {
      storage.removeItem(doomed[j])
    }
  } catch (e) {
    // ignore
  }
  return doomed
}

/**
 * Best-effort expiry of every JS-readable cookie.
 *
 * `xolas_auth` is HttpOnly (XOL-144/145), so it is invisible here and cannot
 * be removed from script — the API clears it server-side via
 * `withClearedAuthCookie` in response to `POST /api/logout`. Secure and
 * `__Host-` cookies are likewise untouchable from an insecure origin. This
 * sweep is for the residue, not for the session cookie.
 */
export function expireJsReadableCookies () {
  if (typeof document === 'undefined') {
    return []
  }

  var raw = ''
  try {
    raw = document.cookie || ''
  } catch (e) {
    return []
  }
  if (!raw) {
    return []
  }

  // window.location is stubbed in some specs and may be missing hostname /
  // pathname, so read both defensively.
  var hostname = ''
  var pathname = '/'
  try {
    if (typeof window !== 'undefined' && window.location) {
      hostname = window.location.hostname || ''
      pathname = window.location.pathname || '/'
    }
  } catch (e) {
    // ignore
  }

  var paths = ['/']
  if (pathname && paths.indexOf(pathname) === -1) {
    paths.push(pathname)
  }

  var domains = ['']
  if (hostname) {
    domains.push(hostname)
    domains.push('.' + hostname)
  }

  var names = []
  var parts = raw.split(';')
  for (var i = 0; i < parts.length; i++) {
    var eq = parts[i].indexOf('=')
    var name = (eq > -1 ? parts[i].slice(0, eq) : parts[i]).trim()
    if (name && names.indexOf(name) === -1) {
      names.push(name)
    }
  }

  var expired = 'expires=Thu, 01 Jan 1970 00:00:00 GMT'
  for (var n = 0; n < names.length; n++) {
    for (var p = 0; p < paths.length; p++) {
      for (var d = 0; d < domains.length; d++) {
        var cookie = names[n] + '=; ' + expired + '; path=' + paths[p]
        if (domains[d]) {
          cookie = cookie + '; domain=' + domains[d]
        }
        try {
          document.cookie = cookie
        } catch (e) {
          // ignore
        }
      }
    }
  }
  return names
}

function runResetHooks () {
  for (var i = 0; i < resetHooks.length; i++) {
    try {
      resetHooks[i].fn()
    } catch (e) {
      // One misbehaving hook must not abort the storage purge below.
      console.error('Session reset hook failed:', resetHooks[i].name, e)
    }
  }
}

/**
 * Read the fuse timestamp. This MUST happen before the storage sweep — the
 * fuse lives under a `xolas-` key, so the sweep clears it, and a fuse read
 * after the sweep would always come back empty and never suppress anything.
 *
 * Returns null when sessionStorage is unavailable, which is treated as
 * "cannot detect a loop" and therefore blocks the reload entirely.
 */
function readFuse () {
  var ss = safeSessionStorage()
  if (!ss) {
    return null
  }
  try {
    return ss.getItem(TEARDOWN_FUSE_KEY)
  } catch (e) {
    return null
  }
}

function fuseTripped (raw, now, hasStorage) {
  if (!hasStorage) {
    // Without sessionStorage we cannot detect a loop, so never hard-reload.
    return true
  }
  if (!raw) {
    return false
  }
  var then = Number(raw)
  if (isNaN(then)) {
    return false
  }
  return (now - then) < FUSE_WINDOW_MS
}

function armFuse (now) {
  var ss = safeSessionStorage()
  if (!ss) {
    return
  }
  try {
    ss.setItem(TEARDOWN_FUSE_KEY, String(now))
  } catch (e) {
    // ignore
  }
}

/*
The logout reason, latched for the lifetime of the document.

XOL-446. Login.vue used to read `xolas-logout-reason` straight out of
sessionStorage in its own `mounted()` and remove it there — read-once. But
App.vue re-creates `<login>` whenever `showLogin` changes, and it mounts
more than once during a teardown: instrumenting the real app shows
`GET(api-access-denied) → REMOVE → GET(null) → GET(null)`. Whichever
instance happens to survive is usually NOT the one that consumed the key, so
the banner silently disappears. That was survivable while the forced-logout
path was a fragment navigation; once it reloads, the losing case is the
common one and the user is bounced to /login with no explanation.

Latching it here fixes the lifetime: the key is consumed from storage once,
the value lives in module scope (so every later mount in the same document
sees it), and it dies with the document — which is exactly the intended
"show this once, for this logout" semantics.
*/
var latchedLogoutReason
var logoutReasonLatched = false

/*
True once a hard reload has been requested and the document is on its way
out.

This matters because of an ordering trap. `finish()` calls `replaceState`,
which flips `isLoggedIn` to false; App.vue then swaps <home> for <login> on
Vue's nextTick — a microtask that runs AFTER `hardReloadToLogin()` has been
called but BEFORE the browser actually unloads. So a Login instance mounts
in the dying document, and if it consumes `xolas-logout-reason` there, the
key is gone by the time the real post-reload Login mounts: the user lands on
/login with no explanation of why they were kicked out.

So while a reload is pending, `takeLogoutReason()` still reports the reason
(harmless — nobody will see that render) but must NOT remove the key.
*/
var reloadPending = false

/**
 * Read the pending logout reason, consuming it from sessionStorage on the
 * first call. Safe to call from every mount — later calls return the same
 * value rather than null.
 */
export function takeLogoutReason () {
  if (!logoutReasonLatched) {
    logoutReasonLatched = true
    latchedLogoutReason = null
    var ss = safeSessionStorage()
    if (ss) {
      try {
        var stored = ss.getItem(LOGOUT_REASON_KEY)
        if (stored) {
          latchedLogoutReason = stored
          if (reloadPending) {
            // The document is about to be replaced — leave the key for the
            // Login that mounts on the other side of the reload, and do not
            // latch, so that instance reads it fresh.
            logoutReasonLatched = false
          } else {
            ss.removeItem(LOGOUT_REASON_KEY)
          }
        }
      } catch (e) {
        // ignore private-mode failures
      }
    }
  }
  return latchedLogoutReason
}

/**
 * Drop the latched reason. Called on a successful login so a later soft
 * teardown in the same document starts clean.
 */
export function clearLogoutReason () {
  logoutReasonLatched = false
  latchedLogoutReason = null
}

/**
 * Clear the reload fuse. Called on a successful login so the next genuine
 * forced logout is allowed to reload.
 */
export function clearTeardownFuse () {
  var ss = safeSessionStorage()
  if (!ss) {
    return
  }
  try {
    ss.removeItem(TEARDOWN_FUSE_KEY)
  } catch (e) {
    // ignore
  }
}

function hardReloadToLogin () {
  if (typeof window === 'undefined' || !window.location) {
    return false
  }
  try {
    var loc = window.location
    var pathname = loc.pathname || '/'
    var search = loc.search || ''
    var target = pathname + search + '#/login'

    // Point the URL at /login, then reload.
    //
    // `replace()` alone is a FRAGMENT navigation — same document, no reload —
    // which is the bug this teardown exists to fix, so the explicit reload()
    // below is what actually drops the in-memory singletons. Verified in
    // Chrome: after this pair, performance.getEntriesByType('navigation')[0]
    // .type === 'reload'.
    //
    // `replace` rather than `assign` so Back does not land on a half-torn-down
    // state, and rather than history.replaceState so that IF the reload is
    // ever dropped the router still sees a hashchange and shows /login. With
    // replaceState the URL would say /login while the SPA kept rendering the
    // torn-down view — a worse failure mode than a soft teardown.
    if (typeof loc.replace === 'function') {
      loc.replace(target)
    }

    if (typeof loc.reload === 'function') {
      loc.reload()
      return true
    }
  } catch (e) {
    // ignore
  }
  return false
}

/**
 * End the session.
 *
 * @param {String}  reason   'session-expired' | 'api-access-denied' | null.
 *                           Persisted for Login.vue's banner.
 * @param {Boolean} hard     Reload the document once the teardown completes.
 * @param {Boolean} callApi  POST /api/logout first. False from the axios
 *                           interceptor, where the tokens are known-bad and
 *                           we are already inside a response handler.
 * @param {Number}  now      Injectable clock, for tests.
 *
 * Always resolves. Concurrent calls are collapsed — the second is a no-op.
 */
export function endSession (options) {
  var opts = options || {}
  var reason = opts.reason || null
  var hard = !!opts.hard
  var callApi = !!opts.callApi
  var now = typeof opts.now === 'number' ? opts.now : Date.now()

  if (tearingDown) {
    return Promise.resolve(false)
  }

  // (1) Flag first. From here saveStore() is a no-op, so nothing that lands
  //     during the steps below can write live state back into storage.
  tearingDown = true

  // (2) Capture what must survive, before anything is removed. The fuse is
  //     read here rather than at step (8) because the sweep below clears it
  //     — reading it afterwards would always see an empty fuse and let a
  //     reload loop through.
  var preserved = readPreservedSessionKeys()
  if (reason) {
    preserved[LOGOUT_REASON_KEY] = reason
    // Un-latch first, so a soft teardown in a document that already consumed
    // an earlier reason surfaces this new one rather than the stale latch.
    clearLogoutReason()
    // Write it synchronously as well as restoring it after the sweep. The
    // caller (redirectToLogin) used to set the flash synchronously, and
    // Login.vue's banner is the user-visible half of a forced logout — it
    // should not depend on a network round-trip settling first.
    restorePreserved({ 'xolas-logout-reason': reason })
  }
  var hasSessionStorage = !!safeSessionStorage()
  var fuseAtEntry = readFuse()

  // (3) Network call before the wipe — authLib.logout() reads the refresh
  //     token to build its body. It always resolves and always clears
  //     tokens, on both branches.
  var request
  if (callApi) {
    request = authLib.logout()
  } else {
    try {
      authLib.clearTokens()
    } catch (e) {
      // ignore
    }
    request = Promise.resolve()
  }

  function finish () {
    // (4) In-memory reset BEFORE the storage wipe. Reversed, a callback
    //     landing between the two steps would write live state into the
    //     key we just emptied.
    runResetHooks()

    if (storeRef && typeof storeRef.replaceState === 'function') {
      try {
        var env = null
        if (storeRef.state) {
          env = {
            env: storeRef.state.env,
            endpoint: storeRef.state.endpoint,
            hostname: storeRef.state.hostname,
            copyright: storeRef.state.copyright,
            windowWidth: storeRef.state.windowWidth,
            windowHeight: storeRef.state.windowHeight
          }
        }
        storeRef.replaceState(buildBootState(null, env))
      } catch (e) {
        console.error('Session store reset failed:', e)
      }
    }

    try {
      authLib.setLoginInFlight(false)
    } catch (e) {
      // ignore
    }

    // (5) Storage.
    wipeStorageByPrefix(safeLocalStorage(), [])
    wipeStorageByPrefix(safeSessionStorage(), PRESERVED_SESSION_KEYS)

    // (6) Put the survivors back (the reason may be newly set this call).
    restorePreserved(preserved)

    // (7) Cookies, best-effort.
    expireJsReadableCookies()

    // (8) Reload last, and only when it cannot loop.
    if (hard) {
      if (fuseTripped(fuseAtEntry, now, hasSessionStorage)) {
        console.warn('Session teardown: reload suppressed, another hard teardown ran within ' + FUSE_WINDOW_MS + 'ms.')
      } else {
        armFuse(now)
        // Set BEFORE requesting the reload: Vue's pending re-render (which
        // mounts <login> and would otherwise consume the logout reason) runs
        // after this task, while the document is still alive.
        reloadPending = true
        if (hardReloadToLogin()) {
          // Document is on its way out; leave the flags set so nothing
          // writes or consumes during unload.
          return true
        }
        reloadPending = false
      }
    }

    tearingDown = false
    return true
  }

  return request.then(finish, finish)
}

/*
Test-only reset.

`tearingDown` is deliberately left set after a successful hard reload — the
document is on its way out and nothing should write during unload. jsdom
never unloads, so without this the flag stays set and every later
endSession() in the same module instance short-circuits.

Pass `{ keepHooks: true }` from specs that rely on the hooks modules register
at import time (axiosClient, appConfig) — those registrations happen once and
cannot be re-run.
*/
/* istanbul ignore next */
export function _resetSessionForTests (opts) {
  tearingDown = false
  reloadPending = false
  clearLogoutReason()
  if (opts && opts.keepHooks) {
    return
  }
  storeRef = null
  resetHooks.length = 0
}

export default {
  STORAGE_PREFIX: STORAGE_PREFIX,
  USER_STATE_KEY: USER_STATE_KEY,
  CONFIG_CACHE_KEY: CONFIG_CACHE_KEY,
  POST_LOGIN_REDIRECT_KEY: POST_LOGIN_REDIRECT_KEY,
  LOGOUT_REASON_KEY: LOGOUT_REASON_KEY,
  TEARDOWN_FUSE_KEY: TEARDOWN_FUSE_KEY,
  PRESERVED_SESSION_KEYS: PRESERVED_SESSION_KEYS,
  setStore: setStore,
  registerResetHook: registerResetHook,
  isTearingDown: isTearingDown,
  buildBootState: buildBootState,
  clearTeardownFuse: clearTeardownFuse,
  takeLogoutReason: takeLogoutReason,
  clearLogoutReason: clearLogoutReason,
  endSession: endSession
}
